import React from 'react';
import { Typography, Card, Row, Col } from 'antd';
import moment from 'moment';
import styled from '@emotion/styled';

const { Title, Text } = Typography;

const StyledCard = styled(Card)`
  background-color: #f0f8ff;
  border: 2px solid #1890ff;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-bottom: 24px;
`;

const ResultTitle = styled(Title)`
  color: #1890ff;
  margin-bottom: 12px !important;
`;

const HighlightText = styled(Text)`
  font-size: 14px;
  font-weight: bold;
`;

const ResultRow = styled(Row)`
  margin-bottom: 4px;
`;

const ResultsDisplay = ({
  calculationType,
  finalMonth,
  startDate,
  cumulativeTotals,
  currency,
  currencyFormatter,
  lastCalculatedValues,
}) => {
  if (!finalMonth || !lastCalculatedValues) {
    return null;
  }

  const ResultContent = calculationType === 'mortgage'
    ? <MortgageResults 
        finalMonth={finalMonth}
        cumulativeTotals={cumulativeTotals}
        currency={currency}
        currencyFormatter={currencyFormatter}
        lastCalculatedValues={lastCalculatedValues}
      />
    : calculationType === 'debt' 
      ? <DebtResults 
          finalMonth={finalMonth}
          startDate={startDate}
          cumulativeTotals={cumulativeTotals}
          currency={currency}
          currencyFormatter={currencyFormatter}
          lastCalculatedValues={lastCalculatedValues}
        />
      : <InvestmentResults 
          finalMonth={finalMonth}
          cumulativeTotals={cumulativeTotals}
          currency={currency}
          currencyFormatter={currencyFormatter}
          lastCalculatedValues={lastCalculatedValues}
        />;

  return (
    <StyledCard>
      <ResultTitle level={4}>
        {calculationType === 'mortgage' 
            ? 'Mortgage Repayment Summary' 
            : calculationType === 'debt' 
              ? 'Debt Repayment Summary' 
              : 'Investment Growth Summary'}
      </ResultTitle>
      {ResultContent}
    </StyledCard>
  );
};

const MortgageResults = ({ 
  finalMonth, 
  cumulativeTotals, 
  currency, 
  currencyFormatter,
  lastCalculatedValues,
}) => {
  const {
    propertyValue,
    mortgageAmount,
    deposit,
    fixedRate,
    fixedTerm,
    totalTerm,
    variableRate,
    mortgageType,
    // Overpayment info
    termReduction,
    interestSaved,
    originalTerm,
    newTerm
  } = lastCalculatedValues;

  const monthlyPaymentFixed = lastCalculatedValues.monthlyPaymentFixed;
  const monthlyPaymentVariable = lastCalculatedValues.monthlyPaymentVariable;

  // Calculate term reduction in a readable format
  const termReductionText = termReduction ? 
  (termReduction >= 12 ? 
    `${Math.floor(termReduction/12)}y ${termReduction%12}m` : 
    `${termReduction}m`) : 
  'None';

  return (
    <>
      <ResultRow>
        <Col span={12}>Property Value:</Col>
        <Col span={12}>
          <HighlightText>
            {currencyFormatter(propertyValue, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Deposit:</Col>
        <Col span={12}>
          <HighlightText type="success">
            {currencyFormatter(deposit, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Loan Amount:</Col>
        <Col span={12}>
          <HighlightText>
            {currencyFormatter(mortgageAmount, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Mortgage Type:</Col>
        <Col span={12}>
          <HighlightText>
            {mortgageType === 'repayment' ? 'Repayment' : 'Interest Only'}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Fixed Rate Period:</Col>
        <Col span={12}>
          <HighlightText>
            {fixedTerm} years at {fixedRate}%
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Variable Rate Period:</Col>
        <Col span={12}>
          <HighlightText>
            {totalTerm - fixedTerm} years at {variableRate}%
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Monthly Payment (Fixed Period):</Col>
        <Col span={12}>
          <HighlightText>
            {currencyFormatter(monthlyPaymentFixed, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Monthly Payment (Variable Period):</Col>
        <Col span={12}>
          <HighlightText>
            {currencyFormatter(monthlyPaymentVariable, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Total Interest Paid:</Col>
        <Col span={12}>
          <HighlightText type="warning">
            {currencyFormatter(cumulativeTotals.totalMortgageInterest, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Total Payments:</Col>
        <Col span={12}>
          <HighlightText type="success">
            {currencyFormatter(cumulativeTotals.totalMortgagePayments, currency)}
          </HighlightText>
        </Col>
      </ResultRow>
      {cumulativeTotals.totalOverpayments > 0 && (
        <>
          <ResultRow>
            <Col span={12}>Total Overpayments:</Col>
            <Col span={12}>
              <HighlightText type="success">
                {currencyFormatter(cumulativeTotals.totalOverpayments, currency)}
              </HighlightText>
            </Col>
          </ResultRow>
          <ResultRow>
            <Col span={12}>Interest Saved:</Col>
            <Col span={12}>
              <HighlightText type="success">
                {currencyFormatter(interestSaved || 0, currency)}
              </HighlightText>
            </Col>
          </ResultRow>
          <ResultRow>
            <Col span={12}>Term Reduction:</Col>
            <Col span={12}>
              <HighlightText type="success">
                {termReductionText}
              </HighlightText>
            </Col>
          </ResultRow>
        </>
      )}
      <ResultRow>
        <Col span={12}>Total Mortgage Term:</Col>
        <Col span={12}>
          <HighlightText>
            {totalTerm} years
          </HighlightText>
        </Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Payoff Date:</Col>
        <Col span={12}>
          <HighlightText>
            {finalMonth.format('MMMM YYYY')}
          </HighlightText>
        </Col>
      </ResultRow>
    </>
  );
};

const DebtResults = ({ 
  finalMonth, 
  startDate, 
  cumulativeTotals, 
  currency, 
  currencyFormatter,
  lastCalculatedValues,
}) => {
  const {
    additionalSpending,
    calculatedContribution,
    targetMonths,
  } = lastCalculatedValues;

  const totalMonths = finalMonth.diff(moment(startDate), 'months');
  const years = Math.floor(totalMonths / 12);
  const remainingMonths = totalMonths % 12;

  const payoffTime = targetMonths 
    ? (targetMonths > 12 
        ? `${Math.floor(targetMonths / 12)}y ${targetMonths % 12}m`
        : `${targetMonths}m`)
    : (years > 0 ? `${years}y ` : '') + `${remainingMonths}m`;

  return (
    <>
      <ResultRow>
        <Col span={12}>Payoff Time:</Col>
        <Col span={12}><HighlightText type="success">{payoffTime}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Monthly Payment:</Col>
        <Col span={12}><HighlightText>{currencyFormatter(calculatedContribution, currency)}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Debt-free Date:</Col>
        <Col span={12}><HighlightText>{finalMonth.format('MMM YYYY')}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Total Interest:</Col>
        <Col span={12}><HighlightText type="warning">{currencyFormatter(cumulativeTotals.totalInterestCharged, currency)}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Total Payments:</Col>
        <Col span={12}><HighlightText type="success">{currencyFormatter(cumulativeTotals.totalPaymentsMade, currency)}</HighlightText></Col>
      </ResultRow>
    </>
  );
};

const InvestmentResults = ({ 
  finalMonth, 
  cumulativeTotals, 
  currency, 
  currencyFormatter,
  lastCalculatedValues,
}) => {
  const {
    years,
    months,
    contributionMonths,
    targetEndBalance,
    calculatedContribution,
    finalBalance
  } = lastCalculatedValues;

  const totalMonths = parseInt(years) * 12 + parseInt(months || 0);
  const investmentTime = `${years}y ${months || 0}m`;

  return (
    <>
      <ResultRow>
        <Col span={12}>Investment time horizon:</Col>
        <Col span={12}><HighlightText>{investmentTime}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Your monthly contribution:</Col>
        <Col span={12}><HighlightText>{currencyFormatter(calculatedContribution, currency)}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Your investments final balance:</Col>
        <Col span={12}><HighlightText type="success">{currencyFormatter(finalBalance, currency)}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Target Date:</Col>
        <Col span={12}><HighlightText>{finalMonth.format('MMM YYYY')}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Your investments total growth:</Col>
        <Col span={12}><HighlightText type="success">{currencyFormatter(cumulativeTotals.totalGrowth, currency)}</HighlightText></Col>
      </ResultRow>
      <ResultRow>
        <Col span={12}>Your total contributions:</Col>
        <Col span={12}><HighlightText type="success">{currencyFormatter(cumulativeTotals.totalContributions, currency)}</HighlightText></Col>
      </ResultRow>
      {targetEndBalance && (
        <ResultRow>
          <Col span={12}>Target Met:</Col>
          <Col span={12}>
            <HighlightText type={Math.abs(finalBalance - targetEndBalance) < 0.01 ? "success" : "warning"}>
              {Math.abs(finalBalance - targetEndBalance) < 0.01 ? "Yes" : "No"}
            </HighlightText>
          </Col>
        </ResultRow>
      )}
    </>
  );
};

export default ResultsDisplay;